import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { IParticipationSummary } from "@Interfaces";
import { MainSelector, ParticipationActions, useAppDispatch } from "@Store";
import { ParticipationUsageService } from "@Services/API/Participation";
import {
    participationCardSx,
    participationIconChipSx,
    participationValueSx,
    PARTICIPATION_BODY_TEXT,
    PARTICIPATION_CONTROL_HEIGHT,
    PARTICIPATION_ICON_SIZE,
    PARTICIPATION_ICON_STROKE,
    PARTICIPATION_INDIGO,
    PARTICIPATION_INDIGO_HOVER,
    PARTICIPATION_MUTED_TEXT,
    PARTICIPATION_ROW_HEIGHT,
    PARTICIPATION_TEXT_LG,
    PARTICIPATION_TEXT_MD,
    PARTICIPATION_TEXT_SM,
    PARTICIPATION_TEXT_XS,
    PARTICIPATION_WARNING_TEXT,
} from "./participationStyles";

interface AttendeeWidgetProps {
    channelId: string;
    summary: IParticipationSummary;
}

type AttendeeWidgetMode = "empty" | "editing" | "filled";

const peopleIconSx = {
    width: PARTICIPATION_ICON_SIZE,
    height: PARTICIPATION_ICON_SIZE,
};

export const AttendeeWidget: React.FC<AttendeeWidgetProps> = ({
    channelId,
    summary,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isSaving = MainSelector(
        (state) => state.participation.isSavingAttendeeCount
    );
    const serverError = MainSelector(
        (state) => state.participation.attendeeCountError
    );

    const [mode, setMode] = useState<AttendeeWidgetMode>(
        summary.attendee_count ? "filled" : "empty"
    );
    const [draft, setDraft] = useState("");
    // Checked before saving so the common cases are translated; the API re-validates.
    const [validationError, setValidationError] = useState<string | null>(null);
    const errorMessage = validationError ?? serverError;

    useEffect(() => {
        if (summary.attendee_count && mode !== "editing") {
            setMode("filled");
        }
    }, [summary.attendee_count, mode]);

    const startEdit = () => {
        setValidationError(null);
        setDraft(summary.attendee_count ? String(summary.attendee_count) : "");
        setMode("editing");
    };

    const cancel = () => {
        setValidationError(null);
        dispatch(ParticipationActions.clearAttendeeCountError());
        setMode(summary.attendee_count ? "filled" : "empty");
    };

    const save = async () => {
        // The API accepts only a whole number of at least 1, sent as a JSON number.
        const count = Number(draft.trim());
        if (!draft.trim() || !Number.isSafeInteger(count) || count < 1) {
            setValidationError(
                t("pages.participation.widgets.attendee.errorInvalid")
            );
            return;
        }
        if (count < summary.respondents) {
            setValidationError(
                t("pages.participation.widgets.attendee.errorTooFew", {
                    respondents: summary.respondents,
                })
            );
            return;
        }
        setValidationError(null);
        const previousCount = summary.attendee_count;
        const succeeded = await dispatch(
            ParticipationActions.setAttendeeCount(
                channelId,
                count,
                t("pages.participation.widgets.attendee.errorSave")
            )
        );
        if (succeeded) {
            setMode("filled");
            if (count !== previousCount) {
                ParticipationUsageService.reportAttendeeCountChanged(
                    channelId,
                    count,
                    previousCount
                );
            }
        }
    };

    const noAnswers = summary.respondents === 0;
    const percentage = summary.attendee_count
        ? Math.min(
              100,
              Math.round((summary.respondents / summary.attendee_count) * 100)
          )
        : 0;

    // Same card as the other tiles; only the empty/editing state is dashed.
    const cardSx =
        mode === "filled"
            ? participationCardSx
            : {
                  ...participationCardSx,
                  backgroundColor: "#F7F8FE",
                  border: "2px dashed #9BA3E8",
              };

    return (
        <Box sx={cardSx}>
            <Box sx={participationIconChipSx}>
                <svg
                    style={peopleIconSx}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={PARTICIPATION_ICON_STROKE}
                    strokeWidth={2}
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 3a9 9 0 0 1 9 9h-9z" />
                </svg>
            </Box>

            {mode === "empty" && (
                <>
                    <Box sx={participationValueSx(true)}>–%</Box>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_LG,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {t("pages.participation.widgets.attendee.label")}
                    </Box>
                    <Button
                        onClick={startEdit}
                        sx={{
                            mt: 0.5,
                            height: PARTICIPATION_CONTROL_HEIGHT,
                            px: 2,
                            borderRadius: "8px",
                            backgroundColor: PARTICIPATION_INDIGO,
                            color: "#FFFFFF",
                            fontSize: PARTICIPATION_TEXT_SM,
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: PARTICIPATION_INDIGO_HOVER,
                            },
                        }}
                    >
                        + {t("pages.participation.widgets.attendee.addButton")}
                    </Button>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_XS,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {noAnswers
                            ? t("pages.participation.widgets.attendee.hintNone")
                            : t(
                                  "pages.participation.widgets.attendee.hintSome",
                                  { respondents: summary.respondents }
                              )}
                    </Box>
                </>
            )}

            {mode === "editing" && (
                <>
                    <Box
                        component="label"
                        htmlFor="participation-attendee-input"
                        sx={{
                            fontSize: PARTICIPATION_TEXT_MD,
                            fontWeight: 700,
                        }}
                    >
                        {noAnswers
                            ? t(
                                  "pages.participation.widgets.attendee.questionExpect"
                              )
                            : t(
                                  "pages.participation.widgets.attendee.questionAttended"
                              )}
                    </Box>
                    <Box
                        component="input"
                        id="participation-attendee-input"
                        type="number"
                        min={1}
                        inputMode="numeric"
                        placeholder={t(
                            "pages.participation.widgets.attendee.placeholder"
                        )}
                        value={draft}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setDraft(e.target.value)
                        }
                        sx={{
                            font: "inherit",
                            width: "100%",
                            maxWidth: 180,
                            boxSizing: "border-box",
                            height: PARTICIPATION_ROW_HEIGHT,
                            padding: "0 12px",
                            borderRadius: "8px",
                            border: errorMessage
                                ? "2px solid #D98E1F"
                                : "2px solid #9BA3E8",
                            fontSize: 18,
                            fontWeight: 700,
                            textAlign: "center",
                            color: PARTICIPATION_BODY_TEXT,
                            backgroundColor: "#FFFFFF",
                        }}
                    />
                    {errorMessage ? (
                        <Box
                            role="alert"
                            sx={{
                                fontSize: PARTICIPATION_TEXT_XS,
                                color: PARTICIPATION_WARNING_TEXT,
                            }}
                        >
                            {errorMessage}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                fontSize: PARTICIPATION_TEXT_XS,
                                color: PARTICIPATION_MUTED_TEXT,
                            }}
                        >
                            {t("pages.participation.widgets.attendee.helper")}
                        </Box>
                    )}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            onClick={cancel}
                            disabled={isSaving}
                            sx={{
                                height: PARTICIPATION_CONTROL_HEIGHT,
                                px: 1.75,
                                borderRadius: "8px",
                                border: "1px solid #C9CEF0",
                                backgroundColor: "#FFFFFF",
                                color: "#4A5568",
                                fontSize: PARTICIPATION_TEXT_SM,
                                textTransform: "none",
                            }}
                        >
                            {t("pages.participation.widgets.attendee.cancel")}
                        </Button>
                        <Button
                            onClick={save}
                            disabled={isSaving}
                            sx={{
                                height: PARTICIPATION_CONTROL_HEIGHT,
                                px: 2,
                                borderRadius: "8px",
                                backgroundColor: PARTICIPATION_INDIGO,
                                color: "#FFFFFF",
                                fontSize: PARTICIPATION_TEXT_SM,
                                fontWeight: 700,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: PARTICIPATION_INDIGO_HOVER,
                                },
                            }}
                        >
                            {t("pages.participation.widgets.attendee.save")}
                        </Button>
                    </Box>
                </>
            )}

            {mode === "filled" && (
                <>
                    <Box sx={participationValueSx(noAnswers)}>
                        {noAnswers ? "–%" : `${percentage}%`}
                    </Box>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_LG,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {t("pages.participation.widgets.attendee.label")}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: PARTICIPATION_TEXT_SM,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        <Box component="span">
                            {noAnswers
                                ? t(
                                      "pages.participation.widgets.attendee.subNoAnswers",
                                      { attendees: summary.attendee_count }
                                  )
                                : t(
                                      "pages.participation.widgets.attendee.sub",
                                      {
                                          respondents: summary.respondents,
                                          attendees: summary.attendee_count,
                                      }
                                  )}
                        </Box>
                        <Box component="span" aria-hidden="true">
                            ·
                        </Box>
                        <Button
                            onClick={startEdit}
                            aria-label={t(
                                "pages.participation.widgets.attendee.editAria"
                            )}
                            sx={{
                                border: 0,
                                backgroundColor: "transparent",
                                padding: "8px 4px",
                                color: PARTICIPATION_INDIGO,
                                fontSize: PARTICIPATION_TEXT_SM,
                                textDecoration: "underline",
                                textTransform: "none",
                                minWidth: "auto",
                            }}
                        >
                            {t("pages.participation.widgets.attendee.edit")}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};
